"use client";
import { useState } from "react";
import {
  CheckCircle,
  ArrowRight,
  Bolt,
  Star,
  Users,
  Zap,
  FileText,
} from "lucide-react";
import {
  accessControlConsoleApi,
  accessWebAppApi,
} from "../../services/authService";
import "../portalSelector/portalSelector.css";
import { handleApiError } from "../shared/utils";

export default function PortalSelector() {
  const [isLoading, setIsLoading] = useState(false);

  const getAccessLevel = () => {
    const level = sessionStorage.getItem("accessLevel");
    if (level == 0) {
      return "assessment-only";
    } else {
      return "dual-access";
    }
  };

  const [userType, setUserType] = useState(getAccessLevel());

  const userTypes = [
    {
      id: 1,
      title: "Assessment Taker",
      description:
        "Complete comprehensive soft skills assessments and unlock personalized career insights",
      icon: Star,
      highlight: true,
    },
    {
      id: 2,
      title: "Business User",
      description:
        "Invite participants, monitor progress, and gain strategic workforce insights",
      icon: Users,
      highlight: false,
    },
    {
      id: 3,
      title: "Dual Access",
      description:
        "Access both platforms with full capabilities for complete talent intelligence",
      icon: Bolt,
      highlight: false,
    },
  ];

  const assessmentSteps = [
    {
      id: 1,
      title: "Complete Your Sprints",
      description:
        "Finish 3 adaptive sprints with 27 questions that evaluate your soft skills and problem-solving approach in just 10–15 minutes.",
      icon: Zap,
    },
    {
      id: 2,
      title: "Build Your Career Profile",
      description:
        "Proceed to the Career Profile Assessment and upload your resume to enable deeper insights and contextual analysis.",
      icon: FileText,
    },
    {
      id: 3,
      title: "Get Your Personalized Report",
      description:
        "Receive a comprehensive report with career recommendations, a soft skill analysis, and a custom learning plan designed to accelerate your growth.",
      icon: Star,
    },
  ];

  const handleNavigateToMayaMaya = async () => {
    setIsLoading(true);
    try {
      const obj = {
        email: sessionStorage.getItem("email"),
      };
      const response = await accessWebAppApi(obj);
      if (response.code === 200) {
        setIsLoading(false);
        window.location.href = response.data.url;
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleNavigateToConsole = async () => {
    setIsLoading(true);
    try {
      const obj = {
        email: sessionStorage.getItem("email"),
      };
      const response = await accessControlConsoleApi(obj);
      if (response.code === 200) {
        setIsLoading(false);

        window.location.href = response.data.url;
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const getTimelineItems = () => {
    if (userType === "assessment-only") {
      return assessmentSteps;
    }
    return userTypes;
  };

  return (
    <div className="upskill-wrapper">
      <div className="upskill-bg-container">
        <div className="upskill-gradient-blob upskill-blob-1"></div>
        <div className="upskill-gradient-blob upskill-blob-2"></div>
      </div>

      <div className="upskill-main-content">
        <div className="portal-left-column">
          <div className="upskill-header">
            <h1 className="upskill-title">
              {userType === "assessment-only"
                ? "Welcome to MayaMaya Assessment"
                : "Welcome to MayaMaya"}
            </h1>
            <p className="upskill-subtitle">
              {userType === "assessment-only"
                ? "Complete comprehensive assessments and unlock personalized career insights tailored to your growth."
                : "A unified platform for talent assessment and management. Choose your path or access both."}
            </p>
          </div>

          <div className="upskill-progress-container">
            <div className="upskill-timeline">
              {getTimelineItems().map((timelineItem, index) => {
                const Icon = timelineItem.icon;
                const timelineItems = getTimelineItems();

                return (
                  <div key={timelineItem.id} className="upskill-timeline-item">
                    {/* Timeline line */}
                    {index < timelineItems.length - 1 && (
                      <div className="upskill-timeline-line upskill-timeline-line-active"></div>
                    )}

                    {/* Timeline node */}
                    <div className="upskill-timeline-node-wrapper">
                      <div
                        className={`upskill-timeline-node upskill-node-active ${
                          timelineItem.highlight ? "upskill-node-highlight" : ""
                        }`}
                      >
                        <Icon size={24} className="upskill-node-icon" />
                      </div>
                    </div>

                    {/* Step content */}
                    <div className="upskill-step-content-wrapper">
                      <div className="upskill-step-header">
                        <h3 className="upskill-step-title">
                          {timelineItem.title}
                        </h3>
                      </div>
                      <p className="upskill-step-description">
                        {timelineItem.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="upskill-action-section">
              <div className="upskill-action-card">
                <div className="upskill-action-content">
                  <h3 className="upskill-action-title">
                    {userType === "assessment-only"
                      ? "Start Your Journey"
                      : "Ready to get started?"}
                  </h3>
                  <p className="upskill-action-description">
                    {userType === "assessment-only"
                      ? "Begin your assessment journey and discover your potential through AI-powered insights"
                      : "Select your role below to access the right tools and features for your needs"}
                  </p>
                </div>
              </div>

              <div className="upskill-button-group">
                <button
                  className="upskill-action-button upskill-button-primary"
                  onClick={handleNavigateToMayaMaya}
                  disabled={isLoading}
                >
                  <span className="upskill-button-text">
                    {isLoading ? "Redirecting..." : "Start Assessment"}
                  </span>
                  <ArrowRight size={18} className="upskill-button-icon" />
                </button>
                {userType === "dual-access" && (
                  <button
                    className="upskill-action-button upskill-button-secondary"
                    onClick={handleNavigateToConsole}
                    disabled={isLoading}
                  >
                    <span className="upskill-button-text">
                      {isLoading ? "Loading..." : "Access Business Console"}
                    </span>
                    <ArrowRight size={18} className="upskill-button-icon" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="portal-right-column">
          <div className="portal-info-card portal-info-card-assessment">
            <div className="portal-card-header">
              <div className="logo-container">
                <svg
                  width="100"
                  height="50"
                  viewBox="0 0 120 60"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="career-auth-logo"
                >
                  <path
                    d="M37.0024 0.946045L36.9688 1.02501V1.03354L37.0024 0.946045Z"
                    fill="currentColor"
                  />
                  <path
                    d="M52.4023 23.4972L52.4163 23.5281L52.4302 23.4929L52.4023 23.4972Z"
                    fill="currentColor"
                  />
                  <mask
                    id="mask0_3147_16711"
                    maskUnits="userSpaceOnUse"
                    x="10"
                    y="2"
                    width="100"
                    height="58"
                  >
                    <path
                      d="M110 2.82495H10V59.054H110V2.82495Z"
                      fill="white"
                    />
                  </mask>
                  <g mask="url(#mask0_3147_16711)">
                    <path
                      d="M99.824 7.49986L94.348 23.3389L102.302 45.1929H97.339L92.088 28.8199L86.365 45.1909H81.957C85.804 34.6299 93.532 13.3949 97.379 2.82886H102.306L109.999 23.9599L105.04 23.9709L99.824 7.49986Z"
                      fill="currentColor"
                    />
                    <path
                      d="M89.6626 24.0261L89.6516 24.0581L89.6406 24.0311L89.6626 24.0261Z"
                      fill="currentColor"
                    />
                    <path
                      d="M43.281 7.49195L48.471 23.937L53.43 23.929L45.754 2.82495H40.829C40.788 2.94395 35.638 17.125 35.6 17.241L30.362 2.82495H25.378C21.547 13.391 13.832 34.625 10 45.191H14.408L20.135 28.725L25.36 45.195H29.86L30.091 44.563L35.557 28.817L40.787 45.195H45.749L37.824 23.378C37.838 23.343 43.268 7.52395 43.281 7.49195ZM27.881 38.492C27.873 38.465 22.391 23.409 22.381 23.372L27.844 7.47195L33.109 24.108L27.881 38.492Z"
                      fill="currentColor"
                    />
                    <path
                      d="M63.8158 7.48186L58.3528 23.3249L66.3118 45.1889L61.3528 45.1939L56.1088 28.8249L50.3748 45.1679H45.9648C49.8188 34.5929 57.5358 13.3989 61.3888 2.82886H66.3158L73.9928 23.9289L69.0338 23.9369L63.8158 7.48186Z"
                      fill="currentColor"
                    />
                    <path
                      d="M92.0309 2.87248C86.0059 19.3265 77.5769 42.6405 71.6149 59.0555H67.2419L72.0939 45.2285C72.0939 45.2285 65.3449 28.3135 63.9609 24.1285H68.4609C68.4609 24.1425 74.1109 40.1455 74.1159 40.1635L79.7999 23.9215C77.9999 19.7325 73.0249 7.00148 71.6249 2.84448H76.1249C76.1249 2.85848 81.9469 18.6005 81.9519 18.6185L87.5959 2.87248H92.0309Z"
                      fill="currentColor"
                    />
                  </g>
                </svg>
              </div>
              <div className="portal-card-title-group">
                <h3 className="portal-card-title">MayaMaya Assessment</h3>
                <p className="portal-card-subtitle">For Assessment Takers</p>
              </div>
            </div>

            <div className="portal-card-content">
              <p className="portal-card-description">
                Discover your potential through AI-powered assessments that
                adapt to your responses. Gain personalized insights into your
                soft skills, track your growth, and uncover career opportunities
                tailored to your strengths and aspirations.
              </p>

              <div className="portal-card-features-section">
                <h4 className="portal-features-title">What You'll Get:</h4>
                <ul className="portal-card-features">
                  <li className="portal-feature-item">
                    <span className="portal-feature-icon">
                      <CheckCircle size={12} />
                    </span>
                    <span className="portal-feature-text">
                      An adaptive journey with 27 interactive questions that
                      evolve based on your answers
                    </span>
                  </li>
                  <li className="portal-feature-item">
                    <span className="portal-feature-icon">
                      <CheckCircle size={12} />
                    </span>
                    <span className="portal-feature-text">
                      Quick 10–15 minute completion time for efficient and
                      engaging participation
                    </span>
                  </li>
                  <li className="portal-feature-item">
                    <span className="portal-feature-icon">
                      <CheckCircle size={12} />
                    </span>
                    <span className="portal-feature-text">
                      Career profile assessment and resume upload for deeper,
                      data-backed insights
                    </span>
                  </li>
                  <li className="portal-feature-item">
                    <span className="portal-feature-icon">
                      <CheckCircle size={12} />
                    </span>
                    <span className="portal-feature-text">
                      AI-generated report with personalized skill analysis,
                      growth areas, and career recommendations
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="portal-info-card portal-info-card-business">
            <div className="portal-card-header">
              <div className="logo-container">
                <svg
                  width="100"
                  height="50"
                  viewBox="0 0 120 60"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="career-auth-logo"
                >
                  <path
                    d="M37.0024 0.946045L36.9688 1.02501V1.03354L37.0024 0.946045Z"
                    fill="currentColor"
                  />
                  <path
                    d="M52.4023 23.4972L52.4163 23.5281L52.4302 23.4929L52.4023 23.4972Z"
                    fill="currentColor"
                  />
                  <mask
                    id="mask0_3147_16711"
                    maskUnits="userSpaceOnUse"
                    x="10"
                    y="2"
                    width="100"
                    height="58"
                  >
                    <path
                      d="M110 2.82495H10V59.054H110V2.82495Z"
                      fill="white"
                    />
                  </mask>
                  <g mask="url(#mask0_3147_16711)">
                    <path
                      d="M99.824 7.49986L94.348 23.3389L102.302 45.1929H97.339L92.088 28.8199L86.365 45.1909H81.957C85.804 34.6299 93.532 13.3949 97.379 2.82886H102.306L109.999 23.9599L105.04 23.9709L99.824 7.49986Z"
                      fill="currentColor"
                    />
                    <path
                      d="M89.6626 24.0261L89.6516 24.0581L89.6406 24.0311L89.6626 24.0261Z"
                      fill="currentColor"
                    />
                    <path
                      d="M43.281 7.49195L48.471 23.937L53.43 23.929L45.754 2.82495H40.829C40.788 2.94395 35.638 17.125 35.6 17.241L30.362 2.82495H25.378C21.547 13.391 13.832 34.625 10 45.191H14.408L20.135 28.725L25.36 45.195H29.86L30.091 44.563L35.557 28.817L40.787 45.195H45.749L37.824 23.378C37.838 23.343 43.268 7.52395 43.281 7.49195ZM27.881 38.492C27.873 38.465 22.391 23.409 22.381 23.372L27.844 7.47195L33.109 24.108L27.881 38.492Z"
                      fill="currentColor"
                    />
                    <path
                      d="M63.8158 7.48186L58.3528 23.3249L66.3118 45.1889L61.3528 45.1939L56.1088 28.8249L50.3748 45.1679H45.9648C49.8188 34.5929 57.5358 13.3989 61.3888 2.82886H66.3158L73.9928 23.9289L69.0338 23.9369L63.8158 7.48186Z"
                      fill="currentColor"
                    />
                    <path
                      d="M92.0309 2.87248C86.0059 19.3265 77.5769 42.6405 71.6149 59.0555H67.2419L72.0939 45.2285C72.0939 45.2285 65.3449 28.3135 63.9609 24.1285H68.4609C68.4609 24.1425 74.1109 40.1455 74.1159 40.1635L79.7999 23.9215C77.9999 19.7325 73.0249 7.00148 71.6249 2.84448H76.1249C76.1249 2.85848 81.9469 18.6005 81.9519 18.6185L87.5959 2.87248H92.0309Z"
                      fill="currentColor"
                    />
                  </g>
                </svg>
              </div>
              <div className="portal-card-title-group">
                <h3 className="portal-card-title">
                  Business Management Interface
                </h3>
                <p className="portal-card-subtitle">For Business Users</p>
              </div>
            </div>

            <div className="portal-card-content">
              <p className="portal-card-description">
                Empower your organization with intelligent talent intelligence.
                Invite participants, monitor assessments in real-time, analyze
                workforce capabilities, and make data-driven decisions for
                strategic growth.
              </p>

              <div className="portal-card-features-section">
                <h4 className="portal-features-title">Key Features:</h4>
                <ul className="portal-card-features">
                  <li className="portal-feature-item">
                    <span className="portal-feature-icon">
                      <CheckCircle size={12} />
                    </span>
                    <span className="portal-feature-text">
                      Invite & Manage Participants
                    </span>
                  </li>
                  <li className="portal-feature-item">
                    <span className="portal-feature-icon">
                      <CheckCircle size={12} />
                    </span>
                    <span className="portal-feature-text">
                      Real-time Progress Monitoring
                    </span>
                  </li>
                  <li className="portal-feature-item">
                    <span className="portal-feature-icon">
                      <CheckCircle size={12} />
                    </span>
                    <span className="portal-feature-text">
                      Advanced Talent Analytics
                    </span>
                  </li>
                  <li className="portal-feature-item">
                    <span className="portal-feature-icon">
                      <CheckCircle size={12} />
                    </span>
                    <span className="portal-feature-text">
                      Strategic Workforce Planning
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
